import { SvgVolumeIcon22, SvgVolumeIconUnActive } from '@/assets/icon';
import SpinLoading from '@/components/loading/SpinLoading';
import { CLAIM_CACHE_TTL, storage } from '@/utils/storage';
import clsx from 'clsx';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CampaignTask, UserTask } from '../api';
import { useClaimCampaign } from '../claim';

import BigNumber from 'bignumber.js';

interface MilestoneItemProps {
    milestone: Partial<UserTask> & {
        isCompleted: boolean;
        isStart: boolean;
        startVolume: number;
        isClaimed: boolean;
        info: CampaignTask | undefined;
    };
    currentVolume: number;
}

const MilestoneItem: React.FC<MilestoneItemProps> = ({
    currentVolume,
    milestone,
}) => {
    const { t, i18n } = useTranslation();

    const { claim, isLoading } = useClaimCampaign();

    const { isCompleted, startVolume, isStart, expected, isClaimed } =
        milestone;

    const [isClaim, setIsClaim] = useState(false);

    const targetVolume = milestone.info?.task_params.threshold ?? 0;

    const isClaimedReward = isClaimed || isClaim;

    // 1. Tính toán phần trăm (Giữ nguyên logic chuẩn)
    const progressPercentage = useMemo(() => {
        if (isCompleted) {
            return 100;
        }
        if (currentVolume <= startVolume) return 0;

        const totalRange = Number(expected) - startVolume || 0;

        const currentProgress = currentVolume - startVolume;

        const percent = (currentProgress / totalRange) * 100;

        return Math.min(100, Math.max(0, percent));
    }, [isCompleted, currentVolume, startVolume, expected]);

    const buttonState = useMemo(() => {
        if (isClaimedReward) {
            return {
                text: t('Claimed'),
                classes: 'bg-[#292929] text-[#666]',
                disabled: true,
            };
        }
        if (isCompleted) {
            return {
                text: t('Claim Reward'),
                classes: 'bg-white text-black shadow-lg',
                disabled: false,
            };
        }

        const text = milestone.info?.task_params.amount_reward
            ? `${BigNumber(milestone.info?.task_params.amount_reward).dividedBy(
                  1e18
              )} WLD`
            : '';
        return {
            text: `${t('Claim')} ${text}`,
            classes: 'bg-[#292929] text-[#666] cursor-not-allowed',
            disabled: true,
        };
    }, [
        isClaimedReward,
        isCompleted,
        milestone.info?.task_params.amount_reward,
        t,
    ]);

    const handleClaim = useCallback(async () => {
        if (
            !milestone.info?.task_params.token_reward ||
            !milestone.call_data ||
            !milestone?.nonce ||
            !milestone?.signature
        ) {
            return;
        }
        const tx = await claim({
            address: milestone.info?.task_params.token_reward,
            callData: milestone?.call_data,
            nonce: milestone?.nonce,
            signal: milestone?.signature,
        });

        if (tx) {
            setIsClaim(true);
            storage.setWithExpiry(
                `volume_claimed_${milestone.id}`,
                true,
                CLAIM_CACHE_TTL
            );
        }
    }, [
        claim,
        milestone.call_data,
        milestone.info?.task_params.token_reward,
        milestone?.nonce,
        milestone?.signature,
        milestone.id,
    ]);

    return (
        <div
            className={clsx('flex w-full ', {
                // Nếu không phải là Start (tức là Mốc 2,3,4,5), cần min-height để vẽ line dài nối lên trên
                // Mốc 1 (Start) chỉ cần height vừa đủ nội dung
                'min-h-[90px] relative': !isStart,
            })}
        >
            {/* --- CỘT 1: TIMELINE --- */}
            <div className="flex flex-col items-center w-[24px] mr-4">
                {/* 1. THE LINE (Quan trọng: Chỉ render ở Mốc 2,3,4,5) */}
                {/* Logic: Line này "mọc" từ đỉnh Mốc hiện tại, vươn lên chạm vào đáy Mốc trước */}
                {!isStart && (
                    <div className="w-0.5 flex-1 bg-[#1C1C1C] relative ">
                        {/* -mt-1: Để đầu line chọc lên trên, nối khít vào icon của mốc trước */}
                        {/* -mb-1: Để chân line cắm vào icon của mốc hiện tại */}

                        <div
                            className={clsx(
                                'absolute top-0 left-0 w-full transition-all duration-300',
                                {
                                    '!h-[92px]': progressPercentage >= 100,
                                }
                            )}
                            style={{
                                height: `${progressPercentage}%`,
                                background:
                                    'linear-gradient(180deg, #17AF00 28.41%, #D9FFD3 100%)',
                            }}
                        />
                    </div>
                )}

                {/* 2. TARGET ICON (Luôn hiển thị) */}
                {/* Nếu là Mốc 1 (isStart): Nó đứng đầu danh sách */}
                {/* Nếu là Mốc 2...: Nó đứng cuối cái Line ở trên */}
                <div
                    className={clsx('z-10 bg-black rounded-full', {
                        // p-[2px] tạo viền đen nhỏ để tách icon khỏi line nếu cần
                        'text-gray-500': !isCompleted && !isClaimedReward,
                        '!text-green-500': isCompleted || isClaimedReward,
                        // Nếu là isStart (Mốc 1), ta có thể chỉnh margin để căn chỉnh nó đẹp hơn nếu cần
                    })}
                >
                    {isCompleted || isClaimedReward ? (
                        <SvgVolumeIcon22 />
                    ) : (
                        <SvgVolumeIconUnActive />
                    )}
                </div>
            </div>

            {/* --- CỘT 2: NỘI DUNG --- */}
            {/* justify-end: Để nội dung luôn nằm ĐÁY component, ngang hàng với Icon */}
            <div
                className={clsx('flex-1 flex flex-col justify-end  mt-6', {
                    'absolute top-[-20px] w-full z-10': isStart,
                })}
            >
                <div
                    className={clsx(
                        'flex justify-between items-center w-full z-10',
                        {
                            'translate-y-5': !isStart,
                        }
                    )}
                >
                    {/* Text Info */}
                    <div
                        className={clsx({
                            'ml-10': isStart,
                        })}
                    >
                        <p className="text-sm text-[#4C4C4C] font-normal mb-0.5">
                            {milestone.info?.title?.[i18n.language] ||
                                milestone.info?.title?.en}
                        </p>
                        <p
                            className={clsx(
                                'text-lg font-medium leading-none',
                                {
                                    'text-white':
                                        isCompleted || isClaimedReward,
                                    'text-[#4C4C4C]': !isCompleted,
                                }
                            )}
                        >
                            {targetVolume?.toLocaleString('en-US')} WLD
                        </p>
                    </div>

                    {/* Button */}
                    <button
                        type="button"
                        className={clsx(
                            'px-4 py-2 text-sm font-bold rounded-full transition-colors duration-200 min-w-[110px]',
                            buttonState.classes,
                            { 'mr-4': isStart }
                        )}
                        onClick={handleClaim}
                        disabled={buttonState.disabled || isLoading}
                    >
                        {isLoading && <SpinLoading className="mx-auto" />}

                        {!isLoading && buttonState.text}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default memo(MilestoneItem);
